import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import AdminNavbar from "../../Components/AdminNavbar/AdminNavbar";
import Footer from "../../../components/footer/Footer";

const AddNewUser = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [adminEmail, setAdminEmail] = useState("");

  // Fetch current logged-in user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("access-token");
      if (!token) {
        setRole("");
        setAdminEmail("");
        return;
      }

      try {
        const res = await fetch(
          "https://backend-server-nine-opal.vercel.app/current-user",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!res.ok) {
          setRole("");
          setAdminEmail("");
          return;
        }

        const data = await res.json();
        if (!data || !data.role) {
          setRole("");
          setAdminEmail("");
          return;
        }

        setRole(data.role);
        setAdminEmail(data.email || "");
      } catch (err) {
        console.error(err);
        setRole("");
        setAdminEmail("");
      }
    };

    fetchCurrentUser();
  }, []);

  // Fetch all users once role is confirmed
  useEffect(() => {
    if (role !== "admin") return;

    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("access-token");
        const res = await fetch(
          "https://backend-server-nine-opal.vercel.app/user",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch users");
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to fetch users", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [role]);

  // Handle Add User form submit
  const handleAddUser = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newUser = {
      name: formData.get("name"),
      email: formData.get("email"),
      password: formData.get("password"),
      role: formData.get("role"),
      adminEmail: adminEmail,
    };

    try {
      const token = localStorage.getItem("access-token");
      const res = await fetch(
        "https://backend-server-nine-opal.vercel.app/user",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(newUser),
        }
      );

      const data = await res.json();
      if (data.success) {
        Swal.fire("Success", "User created", "success");
        setUsers((prev) => [...prev, data.user]);
        e.target.reset();
      } else {
        Swal.fire("Error", data.message || "Failed to create user", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to create user", "error");
    }
  };

  // Handle Delete User
  const handleDeleteUser = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirm.isConfirmed) return;

    try {
      const token = localStorage.getItem("access-token");
      const res = await fetch(
        `https://backend-server-nine-opal.vercel.app/user/${id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (data.success) {
        setUsers((prev) => prev.filter((u) => u._id !== id));
        Swal.fire("Deleted!", "User has been removed.", "success");
      } else {
        Swal.fire("Error", data.error || "Failed to delete user", "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete user", "error");
    }
  };

  // Access denied if not admin
  if (role !== "admin") {
    return (
      <div>
        <AdminNavbar />
        <div className="container mx-auto mt-10 text-center">
          <h2 className="text-xl font-bold">Access Denied</h2>
          <p>Only admins can view this page.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div>
      <AdminNavbar />
      <div className="container mx-auto mt-6 px-4">
        {/* Add New User Form */}
        <div className="mb-6 p-4 border rounded bg-gray-50">
          <h2 className="text-xl font-bold mb-4">Add New User</h2>
          <form onSubmit={handleAddUser}>
            <div className="mb-2">
              <input
                name="name"
                type="text"
                placeholder="Name"
                className="w-full border px-2 py-1 rounded"
                required
              />
            </div>
            <div className="mb-2">
              <input
                name="email"
                type="email"
                placeholder="Email"
                className="w-full border px-2 py-1 rounded"
                required
              />
            </div>
            <div className="mb-2">
              <input
                name="password"
                type="password"
                placeholder="Password"
                className="w-full border px-2 py-1 rounded"
                required
              />
            </div>
            <div className="mb-2">
              <select
                name="role"
                className="w-full border px-2 py-1 rounded"
                required
              >
                <option value="">Select Role</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>
            <button
              type="submit"
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add User
            </button>
          </form>
        </div>

        {/* Users Table */}
        <h1 className="text-2xl font-bold mb-4">All Users</h1>
        {loading ? (
          <p>Loading users...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-auto w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-200 text-left">
                  <th className="border px-4 py-2">Name</th>
                  <th className="border px-4 py-2">Email</th>
                  <th className="border px-4 py-2">Role</th>
                  <th className="border px-4 py-2">Created By</th>
                  <th className="border px-4 py-2">Created At</th>
                  <th className="border px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-100 text-left">
                    <td className="border px-4 py-2">{user.name}</td>
                    <td className="border px-4 py-2">{user.email}</td>
                    <td className="border px-4 py-2 capitalize">{user.role}</td>
                    <td className="border px-4 py-2">
                      {user.createdBy || user.adminEmail || "Admin"}
                    </td>
                    <td className="border px-4 py-2">
                      {new Date(user.createdAt).toLocaleString()}
                    </td>
                    <td className="border px-4 py-2 text-center">
                      <button
                        onClick={() => handleDeleteUser(user._id)}
                        className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default AddNewUser;
